import { queryParam, resolveDisplayUrl } from '../instance/urls.ts';
import { isYouTubeUrl, readInlineLink, readInlinePath } from '../links/inline.ts';
import { readSocialLink } from '../links/social.ts';
import { formatRoute, type Route } from '../links/routes.ts';
import type { RichNode } from '../model/richText.ts';
import { decodeHtmlEntities } from '../text/entities.ts';
import {
  MAX_END_OVERRUN_SEC,
  parseTimecodeSeconds,
  parseTimeParamSeconds,
  TIMECODE_PATTERN,
} from '../text/timecode.ts';
import type { ParseContext } from './context.ts';

/**
 * R8's node tree, built two different ways on purpose.
 *
 * ## Why descriptions do not use `descriptionHtml`
 *
 * Invidious builds `descriptionHtml` from YouTube's *attributed* description: a content string
 * plus link runs carrying `startIndex`/`length`. Those indices are **UTF-16 code unit** offsets
 * and Crystal slices strings by **codepoint**, so every astral character earlier in the text
 * shifts every later run one place to the right. Measured on the live instance, not inferred:
 *
 * - `SqcY0GlETPk` (⚛️ 🚀 … 📖 ahead of the chapter list, two astral) —
 *   `00<a href="/watch?v=…&t=0">:00:00 C</a>ourse Intro` and
 *   `#r<a href="/hashtag/react">eact #</a>r<a href="/hashtag/reactjs">eactjs #</a>…`
 * - `dQw4w9WgXcQ` (📚, one astral) — `#<a href="/hashtag/rickastleynever">RickAstleyNever\n</a>`
 * - `Pz4W67_002M` (no astral) — `<a href="/hashtag/news">#news</a>`, correct.
 *
 * The shift also *leaks* characters across the boundary. An external link's label is re-derived
 * from its href rather than sliced, so the characters the run swallowed surface in the
 * surrounding text instead — which is where the stray `h` in
 * `Check out my full React course: hmosh.link/react-course` comes from, and why the newline after
 * it is gone.
 *
 * The plain `description` is YouTube's own `shortDescription` and is untouched by any of this:
 * full URLs, `#hashtags`, `@handles` and `00:00:00` timecodes exactly as the author typed them.
 * So descriptions are linkified from **plain text**, and `descriptionHtml` is consulted for the
 * one thing plain text cannot express — which channel a mention points at.
 *
 * Showing the real URL instead of YouTube's shortened, sometimes `…`-truncated display text is a
 * bonus rather than a cost: a label that hides where a link goes is a phishing surface.
 *
 * Two other endpoints point the same way. A playlist's `descriptionHtml` can be `<p></p>` while
 * its `description` carries the whole text, and a search channel's carries `<b>` search-term
 * highlighting that the plain field does not.
 *
 * ## Why comments *do* use `contentHtml`
 *
 * Comments come from a different Invidious code path whose anchor boundaries are honest — checked
 * on the wire, the concatenated text of `contentHtml` equals `content` on every comment observed,
 * including ones with astral emoji. And it is the *only* source for a mention's channel ID or a
 * timestamp's target, since the plain text has just `@name` and `8:06`. (That plain text is no
 * cleaner: it is a tag-strip of the same HTML, so it inherits the same leaked `h`.)
 */

// ---------------------------------------------------------------------------
// Node assembly
// ---------------------------------------------------------------------------

interface NodeBuilder {
  /** Appends text verbatim — no line-break handling. */
  text(value: string): void;
  /** Appends text, turning every line ending into a `br` node. */
  textWithBreaks(value: string): void;
  node(value: RichNode): void;
  /**
   * Removes a `#` sitting at the very end of the text written so far, and says whether there was one.
   *
   * Exists for one shape: YouTube does not always put the `#` inside the anchor it linkified, so
   * a hashtag's label can arrive as `news` with the `#` left in the sentence before it. A label is
   * normalised to carry its own `#` — the plain-text front end below builds `#${tag}` and the two must
   * not disagree — and this is what keeps that from rendering as `##news`.
   */
  takeTrailingHash(): boolean;
  finish(): readonly RichNode[];
}

function createNodeBuilder(): NodeBuilder {
  const nodes: RichNode[] = [];
  let pending = '';

  const flush = (): void => {
    if (pending === '') return;
    nodes.push({ t: 'text', s: pending });
    pending = '';
  };

  return {
    text(value: string): void {
      pending += value;
    },
    textWithBreaks(value: string): void {
      // Comments carry `\r\n`, descriptions `\n`; neither ever arrives as `<br>`.
      const lines = value.split(/\r\n|\r|\n/);
      for (let i = 0; i < lines.length; i += 1) {
        if (i > 0) {
          flush();
          nodes.push({ t: 'br' });
        }
        pending += lines[i] ?? '';
      }
    },
    node(value: RichNode): void {
      flush();
      nodes.push(value);
    },
    takeTrailingHash(): boolean {
      if (!pending.endsWith('#')) return false;
      pending = pending.slice(0, -1);
      return true;
    },
    finish(): readonly RichNode[] {
      flush();
      // Trailing breaks are noise on every platform; leading ones shift the whole block down.
      let start = 0;
      let end = nodes.length;
      while (start < end && nodes[start]?.t === 'br') start += 1;
      while (end > start && nodes[end - 1]?.t === 'br') end -= 1;
      return nodes.slice(start, end);
    },
  };
}

// ---------------------------------------------------------------------------
// Anchor classification — shared by both front-ends
// ---------------------------------------------------------------------------

/** What a mention resolves to. Both fields nullable; see `RichNode`'s `mention` arm. */
export interface MentionTarget {
  readonly channelId: string | null;
  readonly handle: string | null;
}

function anchorAttrHref(attrs: string): string {
  const match = /\bhref\s*=\s*"([^"]*)"/i.exec(attrs) ?? /\bhref\s*=\s*'([^']*)'/i.exec(attrs);
  return match?.[1] ?? '';
}

/** `/@handle` → the handle; `null` for any other path. */
function handleFromPath(path: string): string | null {
  const match = /^\/@([^/?#]+)$/.exec(path);
  if (!match) return null;
  const handle = decodeURIComponent(match[1] ?? '');
  return handle === '' ? null : handle;
}

function channelIdFromPath(path: string): string | null {
  const match = /^\/channel\/([^/?#]+)/.exec(path);
  if (!match) return null;
  const id = decodeURIComponent(match[1] ?? '');
  return id === '' ? null : id;
}

function pathOf(target: string): string {
  return target.split('?')[0]?.split('#')[0] ?? '';
}

/**
 * `https://www.youtube.com/redirect?…&q=<target>` — YouTube wraps outbound links in its own
 * tracker. Following it would route every description link through Google, so the real target is
 * unwrapped here rather than at each call site.
 */
function unwrapYouTubeRedirect(url: string): string {
  if (!/^https?:\/\/(?:www\.)?youtube\.com\/redirect\b/i.test(url)) return url;
  const target = queryParam(url, 'q');
  if (target === null || target.trim() === '') return url;
  return /^https?:\/\//i.test(target) ? target : url;
}

/**
 * A route this build can open, as the node that opens it — the one place a {@link Route} becomes a
 * `RichNode`, so both front-ends label a link the same way.
 *
 * Three arms get a **better label than the URL**, and that is most of the point of routing prose
 * links through here: `youtube.com/@LofiGirl` reads as `@LofiGirl`, `youtube.com/c/CBSNews` as
 * `@CBSNews`, and `youtube.com/hashtag/lofi` as `#lofi`. Nothing was looked up to do any of it, since
 * the name is in the path.
 *
 * A **video** and a **channel by id** are the two that cannot be named from the URL alone, so they
 * keep the author's text as `s` and carry the id the platform fetches a title or a name with. That is
 * why they are their own arms rather than a `route`: there is something for the platform to do with
 * them (`model/richText.ts`).
 */
function routeNode(route: Route, label: string): RichNode {
  switch (route.kind) {
    case 'watch':
      return {
        t: 'video',
        s: label,
        videoId: route.videoId,
        startSec: route.startSec,
        playlistId: route.playlistId,
      };
    case 'channel':
      return { t: 'channel', s: label, channelId: route.channelId, path: formatRoute(route) };
    case 'channelName':
      // Every channel reads as `@name`, whichever of the three spellings the URL used. The `@` is
      // what makes a bare word in the middle of a sentence recognisable as a channel, and `/c/NAME`
      // and `/user/NAME` are channels as much as a handle is. It is a mark rather than a claim about
      // the account's handle, which for those two forms the URL does not carry.
      return { t: 'route', s: `@${route.name}`, path: formatRoute(route) };
    case 'hashtag':
      return { t: 'route', s: `#${route.tag}`, path: formatRoute(route) };
    default:
      return { t: 'route', s: label, path: formatRoute(route) };
  }
}

/**
 * An absolute `http(s)` URL as a node: an in-app destination where this build has the page, an
 * outbound link where it does not.
 *
 * An outbound link is **named by where it goes** wherever the host says so (`site`). A description
 * that says "manage it at youtube.com/account" is pointing at a page of YouTube's, and a reader is
 * better off seeing that before pressing than finding out in a new tab. For the eight sites in
 * `links/social.ts` the address shortens to the part that names the destination as well, so a block
 * of social links reads as a list of names rather than a wall of URLs.
 */
function externalUrlNode(url: string, label: string): RichNode {
  const target = unwrapYouTubeRedirect(url);
  const route = readInlineLink(target);
  if (route !== null) return routeNode(route, label);
  if (isYouTubeUrl(target)) return { t: 'link', s: label, href: target, site: 'youtube' };
  const social = readSocialLink(target);
  // The shortened label is `social.label`, and `null` from it means the URL named nothing — the site's
  // front page, or a path with something in it that a label must not hide. The mark still goes on.
  if (social !== null) return { t: 'link', s: social.label ?? label, href: target, site: social.site };
  return { t: 'link', s: label, href: target };
}

/** A label that is nothing but a timecode, which is what an Invidious seek anchor wraps. */
const TIMECODE_LABEL = new RegExp(`^\\s*${TIMECODE_PATTERN}\\s*$`);

/**
 * Turns one anchor into a node. `label` is what the anchor wrapped; the **href** decides the kind,
 * because the href is the one part of an Invidious anchor that is never shifted.
 */
function anchorNode(href: string, label: string, ctx: ParseContext): RichNode {
  const target = decodeHtmlEntities(href).trim();
  if (target === '') return { t: 'text', s: label };

  if (target.startsWith('/')) {
    const path = pathOf(target);

    if (path === '/watch') {
      const raw = queryParam(target, 't');
      const seconds = raw === null ? null : parseTimeParamSeconds(raw);
      /*
       * A seek into *this* video, and the label is what says so: Invidious linkifies a bare `11:20`
       * into `/watch?v=<this video>&t=680`, so the anchor wraps a timecode. A comment linking another
       * video at a position carries a URL as its label, and reading that as a seek would scrub the
       * video being watched to a point in a different one. The `v` cannot tell the two apart — both
       * forms carry it — so the label decides, and a `/watch?t=` with no `v` at all stays a seek
       * because there is nothing else it could mean.
       */
      if (seconds !== null && (queryParam(target, 'v') === null || TIMECODE_LABEL.test(label))) {
        return { t: 'timestamp', s: label, seconds };
      }
    }

    const hashtag = /^\/hashtag\/([^/?#]+)/.exec(path);
    if (hashtag) {
      const tag = decodeURIComponent(hashtag[1] ?? '');
      if (tag !== '') return { t: 'hashtag', s: label, tag };
    }

    const channelId = channelIdFromPath(path);
    if (channelId !== null) return { t: 'mention', s: label, channelId, handle: null };

    const handle = handleFromPath(path);
    if (handle !== null) return { t: 'mention', s: label, channelId: null, handle };

    /*
     * Anything else Invidious routes itself — `/playlist?list=…`, a `/watch?v=` naming another video,
     * a legacy `/cbsnews` vanity path. Where this build has the page, it opens **in the app**; where
     * it does not, the link still works, on the *instance* origin (`resolveDisplayUrl`) and never the
     * transport base (R2).
     */
    const route = readInlinePath(target);
    if (route !== null) return routeNode(route, label);

    const display = resolveDisplayUrl(ctx.config, target);
    return display === null ? { t: 'text', s: label } : { t: 'link', s: label, href: display };
  }

  if (!/^https?:\/\//i.test(target)) return { t: 'text', s: label };
  return externalUrlNode(target, label);
}

// ---------------------------------------------------------------------------
// HTML front-end (comments)
// ---------------------------------------------------------------------------

/** Tags that mean "line break here". Every other tag is dropped — the text inside it survives. */
const BREAK_TAGS = ['br', 'p', '/p', 'div', '/div'];

/**
 * A hashtag node always carries its own `#`. Every other kind passes through untouched.
 *
 * The label is whatever the anchor wrapped, and YouTube is not consistent about where the `#` goes: it
 * is usually inside (`<a href="/hashtag/news">#news</a>`, which is what the live instance answered on
 * every sample looked at on 2026-08-02) and sometimes left in the sentence in front of it. Both shapes
 * have to render like the description's, which builds `#${tag}` from the tag itself — so the label is
 * prefixed and a `#` immediately before the anchor is taken back off the text, or it would read `##news`.
 *
 * The label rather than the tag, because the two legitimately differ in case: `#RickAstleyNever` links
 * to `/hashtag/rickastleynever`, and the author's capitalisation is the part worth showing.
 */
function withHashLabel(node: RichNode, builder: NodeBuilder): RichNode {
  if (node.t !== 'hashtag' || node.s.startsWith('#')) return node;
  builder.takeTrailingHash();
  return { ...node, s: `#${node.s}` };
}

const LEADING_SPACE = /^\s+/;
const TRAILING_SPACE = /\s+$/;

/**
 * End of the tag opened at `open`, skipping quoted attribute values — an href may legitimately
 * contain `>`, and a naive `indexOf('>')` would cut the tag in half and spill the rest of the
 * attribute into the text.
 */
function findTagEnd(html: string, open: number): number {
  let quote = '';
  for (let i = open + 1; i < html.length; i += 1) {
    const char = html.charAt(i);
    if (quote !== '') {
      if (char === quote) quote = '';
      continue;
    }
    if (char === '"' || char === "'") quote = char;
    else if (char === '>') return i;
  }
  return -1;
}

/**
 * A deliberately small scanner rather than an HTML parser. The input is machine-generated by one
 * known producer and the only construct that carries meaning is `<a href>`; everything else is a
 * line break or noise. Unrecognised tags are **dropped**, never re-emitted as text — re-emitting
 * is how a lenient "HTML parser" becomes an XSS bug in a renderer that trusts its own model.
 */
export function parseRichTextHtml(html: string, ctx: ParseContext): readonly RichNode[] {
  const builder = createNodeBuilder();
  let index = 0;

  const emitText = (raw: string): void => {
    if (raw !== '') builder.textWithBreaks(decodeHtmlEntities(raw));
  };

  while (index < html.length) {
    const open = html.indexOf('<', index);
    if (open === -1) {
      emitText(html.slice(index));
      break;
    }
    emitText(html.slice(index, open));

    const close = findTagEnd(html, open);
    // An unterminated tag is not text: emitting the remainder verbatim would put markup on screen.
    if (close === -1) break;

    const inner = html.slice(open + 1, close);
    const name = (/^\/?[a-zA-Z][a-zA-Z0-9]*/.exec(inner)?.[0] ?? '').toLowerCase();

    if (name === 'a') {
      const end = html.toLowerCase().indexOf('</a>', close);
      const rawLabel = decodeHtmlEntities(
        html.slice(close + 1, end === -1 ? html.length : end).replace(/<[^>]*>/g, ''),
      );
      index = end === -1 ? html.length : end + 4;

      // YouTube pads a mention's label for its own pill rendering (`" @FactusCactus "`). The
      // padding belongs to the sentence, not to the chip, so it is re-emitted around the node
      // rather than trimmed away — otherwise `Thanks<a> @X </a>for` renders as `Thanks@Xfor`.
      const lead = LEADING_SPACE.exec(rawLabel)?.[0] ?? '';
      const tail = rawLabel.length === lead.length ? '' : (TRAILING_SPACE.exec(rawLabel)?.[0] ?? '');
      const core = rawLabel.slice(lead.length, rawLabel.length - tail.length);

      const node = anchorNode(anchorAttrHref(inner), core.replace(/[\r\n]+/g, ' '), ctx);
      if (node.t === 'text') {
        builder.textWithBreaks(rawLabel);
      } else {
        builder.textWithBreaks(lead);
        // Before `node()`, which flushes the pending text this may have to take a `#` off the end of.
        const labelled = withHashLabel(node, builder);
        builder.node(labelled);
        builder.textWithBreaks(tail);
      }
      continue;
    }

    if (BREAK_TAGS.includes(name)) builder.node({ t: 'br' });
    index = close + 1;
  }

  return builder.finish();
}

// ---------------------------------------------------------------------------
// Plain-text front-end (descriptions)
// ---------------------------------------------------------------------------

export interface LinkifyOptions {
  /**
   * Length of the video the text belongs to. A timecode past the end is a running time, a
   * broadcast schedule or a score — not a seek target — so it stays plain text, except within
   * {@link MAX_END_OVERRUN_SEC} of the end: an uploader's last stamp may overshoot a trimmed cut
   * by a few seconds, and a click on one of those seeks to wherever the media element clamps it.
   * `null` disables the check; `0`, which every live stream reports, suppresses timestamps
   * entirely and earns no slack, which is the right answer for a stream whose description lists
   * `7:30 AM` programme slots.
   */
  readonly durationSec?: number | null;
  /** Handle (lowercased, no `@`) → channel, from {@link collectMentionTargets}. */
  readonly mentions?: Readonly<Record<string, MentionTarget>>;
}

// Sticky, so each pattern is tried exactly at the cursor. No lookbehind anywhere and no `\p{…}`:
// core has to run under Hermes as well as V8 (R1), and a hand-written boundary check is cheaper
// than depending on either.
const URL_AT = /https?:\/\/[^\s<>"'`]+|www\.[^\s<>"'`]+/iy;
const TIMECODE_AT = new RegExp(TIMECODE_PATTERN, 'y');
const HASHTAG_AT = /#[^\s#@]+/y;
const MENTION_AT = /@[A-Za-z0-9._-]{2,30}/y;

/** Trailing sentence punctuation belongs to the author, not the URL. Brackets close balanced pairs. */
function trimUrlTail(url: string): string {
  let end = url.length;
  while (end > 0) {
    const last = url.charAt(end - 1);
    if ('.,;:!?"\''.includes(last)) {
      end -= 1;
      continue;
    }
    if (last === ')' || last === ']') {
      const open = last === ')' ? '(' : '[';
      const body = url.slice(0, end);
      if (body.split(last).length > body.split(open).length) {
        end -= 1;
        continue;
      }
    }
    break;
  }
  return url.slice(0, end);
}

function trimTagTail(tag: string): string {
  let end = tag.length;
  while (end > 0 && '.,;:!?)]}"\''.includes(tag.charAt(end - 1))) end -= 1;
  return tag.slice(0, end);
}

/** A token may only start where the previous character cannot be part of the same token. */
function startsToken(text: string, at: number, digitsMatter: boolean): boolean {
  if (at === 0) return true;
  const previous = text.charAt(at - 1);
  return digitsMatter ? !/[\d:]/.test(previous) : !/[A-Za-z0-9_@#/]/.test(previous);
}

function lookupMention(
  mentions: Readonly<Record<string, MentionTarget>> | undefined,
  handle: string,
): MentionTarget | null {
  if (mentions === undefined) return null;
  const key = handle.toLowerCase();
  if (!Object.prototype.hasOwnProperty.call(mentions, key)) return null;
  return mentions[key] ?? null;
}

/**
 * Linkifies author-typed plain text: URLs, timecodes, hashtags and `@handles`, in that priority
 * order — so `https://youtube.com/@someone` stays one link rather than a link plus a mention.
 */
export function parseRichTextPlain(text: string, options: LinkifyOptions = {}): readonly RichNode[] {
  const builder = createNodeBuilder();
  const duration = options.durationSec;
  // The slack applies to a real length only — a live stream's `0` stays exact, or the schedule it
  // exists to refuse would gain half a minute. See {@link LinkifyOptions.durationSec}.
  const stampLimit =
    duration === undefined || duration === null
      ? null
      : duration > 0
        ? duration + MAX_END_OVERRUN_SEC
        : duration;

  let index = 0;
  let plainFrom = 0;

  const emitPlain = (upTo: number): void => {
    if (upTo > plainFrom) builder.textWithBreaks(text.slice(plainFrom, upTo));
  };

  while (index < text.length) {
    const char = text.charAt(index);

    if ('hHwW'.includes(char) && startsToken(text, index, false)) {
      URL_AT.lastIndex = index;
      const url = trimUrlTail(URL_AT.exec(text)?.[0] ?? '');
      if (url !== '') {
        emitPlain(index);
        const href = /^www\./i.test(url) ? `https://${url}` : url;
        // The URL as written is the label, and for a link this build opens it is only a fallback: a
        // video's title and a channel's name replace it once the platform holds the record.
        builder.node(externalUrlNode(href, url));
        index += url.length;
        plainFrom = index;
        continue;
      }
    }

    if (char >= '0' && char <= '9' && startsToken(text, index, true)) {
      TIMECODE_AT.lastIndex = index;
      const raw = TIMECODE_AT.exec(text)?.[0] ?? null;
      // `1:23:45:67` is a run of digits and colons, not a timecode with a tail — refuse it whole.
      if (raw !== null && !/[\d:]/.test(text.charAt(index + raw.length))) {
        const seconds = parseTimecodeSeconds(raw);
        if (seconds !== null && (stampLimit === null || seconds <= stampLimit)) {
          emitPlain(index);
          builder.node({ t: 'timestamp', s: raw, seconds });
          index += raw.length;
          plainFrom = index;
          continue;
        }
      }
    }

    if (char === '#' && startsToken(text, index, false)) {
      HASHTAG_AT.lastIndex = index;
      const tag = trimTagTail((HASHTAG_AT.exec(text)?.[0] ?? '').slice(1));
      if (tag !== '') {
        emitPlain(index);
        builder.node({ t: 'hashtag', s: `#${tag}`, tag });
        index += tag.length + 1;
        plainFrom = index;
        continue;
      }
    }

    if (char === '@' && startsToken(text, index, false)) {
      MENTION_AT.lastIndex = index;
      const handle = trimTagTail((MENTION_AT.exec(text)?.[0] ?? '').slice(1));
      // Only a handle the description itself linked becomes a mention. Everything else that looks
      // like one is prose: `@ 3pm`, an email local part, a name on another network.
      const found = handle === '' ? null : lookupMention(options.mentions, handle);
      if (found !== null) {
        emitPlain(index);
        builder.node({
          t: 'mention',
          s: `@${handle}`,
          channelId: found.channelId,
          handle: found.handle ?? handle,
        });
        index += handle.length + 1;
        plainFrom = index;
        continue;
      }
    }

    index += 1;
  }

  emitPlain(text.length);
  return builder.finish();
}

// ---------------------------------------------------------------------------
// Descriptions
// ---------------------------------------------------------------------------

/**
 * Reads every channel anchor out of `descriptionHtml` and keys it by handle.
 *
 * Positions are ignored on purpose — they are the part that drifts. What survives the drift is
 * the href, plus the handle spelled inside the anchor's own label (`   / @amgen  ` for a
 * `/channel/UC…` href). Matching is case-insensitive, which is safe because YouTube handles are
 * unique case-insensitively.
 */
export function collectMentionTargets(html: string): Readonly<Record<string, MentionTarget>> {
  const targets: Record<string, MentionTarget> = {};

  for (const match of html.matchAll(/<a\s([^>]*)>([\s\S]*?)<\/a>/g)) {
    const href = decodeHtmlEntities(anchorAttrHref(match[1] ?? '')).trim();
    if (!href.startsWith('/')) continue;
    const path = pathOf(href);

    const channelId = channelIdFromPath(path);
    const hrefHandle = handleFromPath(path);
    if (channelId === null && hrefHandle === null) continue;

    const label = decodeHtmlEntities((match[2] ?? '').replace(/<[^>]*>/g, ''));
    const labelHandle = /@([A-Za-z0-9._-]{2,30})/.exec(label)?.[1] ?? null;

    for (const handle of [hrefHandle, labelHandle]) {
      if (handle === null) continue;
      const key = handle.toLowerCase();
      const existing = Object.prototype.hasOwnProperty.call(targets, key) ? targets[key] : undefined;
      // A `/channel/UC…` href beats a `/@handle` one: it routes without a second lookup.
      if (existing !== undefined && existing.channelId !== null) continue;
      targets[key] = { channelId, handle: hrefHandle ?? handle };
    }
  }

  return targets;
}

export interface DescriptionOptions {
  /** `lengthSeconds`. Gates timestamps — see {@link LinkifyOptions.durationSec}. */
  readonly durationSec?: number | null;
}

/**
 * The R8 tree for a description: built from the **plain** text, with the HTML consulted only for
 * mention targets. The long reason is at the top of this file; the short one is that
 * `descriptionHtml`'s anchor boundaries shift by the number of astral characters before them and
 * the plain field does not.
 */
export function parseDescriptionNodes(
  description: string,
  descriptionHtml: string,
  options: DescriptionOptions = {},
): readonly RichNode[] {
  return parseRichTextPlain(description, {
    durationSec: options.durationSec ?? null,
    mentions: collectMentionTargets(descriptionHtml),
  });
}

/** Flattens a tree back to its text — for `aria-label`s, copy-to-clipboard, and tests. */
export function richTextToPlain(nodes: readonly RichNode[]): string {
  let out = '';
  for (const node of nodes) out += node.t === 'br' ? '\n' : node.s;
  return out;
}
